import { useCallback, useMemo, useRef } from 'react';

import { clientConfig } from '@/config/clientConfig';
import { useCollectionsApi } from '@/features/collections/hooks/useCollectionsApi';
import { useNotices } from '@/features/notices/useNotices';
import { ItemSignature } from '@/shared/lib/secure-json/core/secure-json-collection/types';
import { useCollectionsStatusStore } from '@/shared/lib/sj-orm/stores/collectionsStatus.store';
import { useSecureJsonCollectionsStore } from '@/shared/lib/sj-orm/stores/secureJsonCollections.store';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';
import { useProfileStore } from '@/shared/store/profile.store';
import { log } from '@/shared/utils/log';

import { DtoId } from '../../models/base.dto';
import { useSyncQueueStore } from '../sync-queue.store';
import { SyncQueueJobMethod } from '../types';

export interface ISynchronizer {
  sync: () => Promise<boolean>;
}

const sortDateDesc = (a: { createdAt: Date }, b: { createdAt: Date }) => {
  return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
};

export const useSynchronizer = (): ISynchronizer => {
  const { keyPair, ...keyPairStore } = useKeyPairStore();
  const profileStore = useProfileStore();

  const statusStore = useCollectionsStatusStore();
  const sjcStore = useSecureJsonCollectionsStore();
  const syncQueueStore = useSyncQueueStore();

  const notice = useNotices();
  const collections = useCollectionsApi();
  const encryptionType = clientConfig.isExported
    ? keyPairStore.encryptionType
    : profileStore.encryptionType;

  const signatureMapRef = useRef(new Map<DtoId, ItemSignature>());

  const sync = useCallback(async (): Promise<boolean> => {
    if (!encryptionType) {
      log.error('[useSynchronizer] empty encryptionType');
      return false;
    }

    log.trace('[useSynchronizer] sync');
    if (!keyPair || syncQueueStore.jobs.length === 0) {
      const kp = `keyPair: ${String(keyPair !== undefined)}`;
      const jobs = `jobs: ${syncQueueStore.jobs.length}`;

      log.trace(`[useSynchronizer] sync skip. ${kp} ${jobs}`);
      return false;
    }

    if (statusStore.syncing) {
      log.trace('[useSynchronizer] sync progress jobs:', syncQueueStore.jobs.length);
      return false;
    } else {
      log.trace('[useSynchronizer] sync start jobs:', syncQueueStore.jobs.length);
    }

    statusStore.setSyncing(true);
    const jobs = syncQueueStore.jobs.sort(sortDateDesc);

    const runJobErrorCase = (
      response: Awaited<
        ReturnType<
          typeof collections.addItem | typeof collections.changeItem | typeof collections.removeItem
        >
      >,
      method: SyncQueueJobMethod,
    ) => {
      notice.error(`[Collections] ${method} error`, { delay: 3 });
      console.error(`[Collections] ${method}`, response);
    };

    try {
      for await (const job of jobs) {
        if (job.data.method === SyncQueueJobMethod.ADD_ITEM) {
          const resAdd = await collections.addItem({
            collectionName: job.data.collectionName,
            item: job.data.item,
          });

          if (resAdd.data?.addItemToCollectionGql) {
            const itemId = job.data.item.decryptedData.id;
            const collection = sjcStore.collections.get(job.data.collectionName);
            const itemData = collection?.getItem(itemId);
            const signature = resAdd.data?.addItemToCollectionGql.signature;

            if (itemData && signature) {
              itemData.signature = signature;
              itemData.decryptedData.signature = signature; // TODO remove signature from item
            } else if (itemId && signature) {
              signatureMapRef.current.set(itemId, signature);
            } else {
              runJobErrorCase(resAdd, SyncQueueJobMethod.ADD_ITEM);
            }
          } else {
            runJobErrorCase(resAdd, SyncQueueJobMethod.ADD_ITEM);
          }

          syncQueueStore.removeJob(job.id);
          continue;
        }

        if (job.data.method === SyncQueueJobMethod.CHANGE_ITEM) {
          const resChange = await collections.changeItem({
            collectionName: job.data.collectionName,
            item: job.data.item,
          });

          if (resChange.data?.changeItemInCollectionGql) {
            const collection = sjcStore.collections.get(job.data.collectionName);
            const itemData = collection?.getItem(job.data.item.decryptedData.id);
            const signature = resChange.data?.changeItemInCollectionGql.signature;

            if (itemData && signature) {
              itemData.signature = signature;
              itemData.decryptedData.signature = signature; // TODO remove signature from item
            }
          } else {
            runJobErrorCase(resChange, SyncQueueJobMethod.CHANGE_ITEM);
          }

          syncQueueStore.removeJob(job.id);
          continue;
        }

        if (job.data.method === SyncQueueJobMethod.REMOVE_ITEM) {
          const { item } = job.data;

          if (!item.signature) {
            item.signature = signatureMapRef.current.get(item.decryptedData.id);
          }

          const resRemove = await collections.removeItem({
            collectionName: job.data.collectionName,
            item,
          });

          if (resRemove.data?.removeItemFromCollectionGql) {
            signatureMapRef.current.delete(item.decryptedData.id);
            syncQueueStore.removeJob(job.id);
          } else {
            runJobErrorCase(resRemove, SyncQueueJobMethod.REMOVE_ITEM);
          }
        }
      }
    } catch (e) {
      statusStore.setSyncing(false);
      log.error('[useSynchronizer] processing jobs', e);
      return false;
    }

    statusStore.setSyncing(false);

    if (useSyncQueueStore.getState().jobs.length === 0) {
      signatureMapRef.current.clear();
    }

    return true;
  }, [statusStore.syncing, encryptionType, keyPair, sjcStore, syncQueueStore.jobs]);

  return useMemo(
    () => ({
      sync,
    }),
    [sync],
  );
};
